import React, { useState } from 'react';
import { GoalIcon, AccentIcon, EditIcon, InsightIcon } from '../../../icons';

interface MissionVisionViewProps {
  goalId: string;
  mission?: string;
  vision?: string;
  onUpdate?: (mission: string, vision: string) => Promise<void>;
  readOnly?: boolean;
}

/**
 * MissionVisionView - Display and optionally edit Mission & Vision
 * 
 * Used in DoelstellingenPage (CollapsibleSection) and JourneyPage
 * Shows mission and vision in a clear, inspiring way
 */
const MissionVisionView: React.FC<MissionVisionViewProps> = ({
  goalId,
  mission,
  vision,
  onUpdate,
  readOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editMission, setEditMission] = useState(mission || '');
  const [editVision, setEditVision] = useState(vision || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasMissionVision = mission || vision;

  const handleSave = async () => {
    if (!onUpdate) return;
    
    const trimmedMission = editMission.trim();
    const trimmedVision = editVision.trim();
    
    if (!trimmedMission || !trimmedVision) {
      setError('Beide velden zijn verplicht');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onUpdate(trimmedMission, trimmedVision);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update mission/vision:', err);
      setError('Opslaan mislukt, probeer het opnieuw');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMission(mission || '');
    setEditVision(vision || '');
    setIsEditing(false);
    setError(null);
  };

  // Empty state
  if (!hasMissionVision && readOnly) {
    return (
      <div className="text-center py-8 text-gray-400">
        <GoalIcon size={32} className="text-red-500 mb-2 mx-auto block" />
        <p className="text-sm">Geen missie en visie gedefinieerd</p>
      </div>
    );
  }

  // Edit mode
  if (isEditing && !readOnly) {
    return (
      <div className="p-6 space-y-6">
        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">
            {error}
          </div>
        )}

        {/* Edit Mission */}
        <div>
          <label className="block mb-2">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base font-medium text-gray-900">
                Missie
              </span>
              <span className="text-sm text-gray-500">Het "waarom"</span>
            </div>
            <textarea
              value={editMission}
              onChange={(e) => setEditMission(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              disabled={isSaving}
            />
          </label>
        </div>

        {/* Edit Vision */}
        <div>
          <label className="block mb-2">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base font-medium text-gray-900">
                Visie
              </span>
              <span className="text-sm text-gray-500">Het "waarheen"</span>
            </div>
            <textarea
              value={editVision}
              onChange={(e) => setEditVision(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              disabled={isSaving}
            />
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>
    );
  }

  // Read mode
  return (
    <div className="p-6">
      {/* Mission Card */}
      <div className="mb-6 bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-6 border border-orange-100">
        <div className="flex items-start gap-3 mb-3">
          <GoalIcon size={24} className="text-red-500" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Missie
            </h3>
            <p className="text-xs text-gray-600 mb-3">Het "waarom" - Jouw bijdrage</p>
            <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
              {mission || <span className="italic text-gray-400">Nog niet ingevuld</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Vision Card */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-100">
        <div className="flex items-start gap-3 mb-3">
          <AccentIcon size={24} className="text-purple-500" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Visie
            </h3>
            <p className="text-xs text-gray-600 mb-3">Het "waarheen" - Jouw toekomstbeeld</p>
            <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
              {vision || <span className="italic text-gray-400">Nog niet ingevuld</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Edit button (if not readOnly and onUpdate available) */}
      {!readOnly && onUpdate && (
        <div className="flex justify-end mt-6">
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors"
          >
            <EditIcon size={14} className="text-orange-600 inline" /> Bewerken
          </button>
        </div>
      )}

      {/* Reminder note */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex gap-3">
          <InsightIcon size={18} className="text-gray-600" />
          <p className="text-sm text-gray-600">
            <strong>Herinnering:</strong> Je missie en visie geven richting aan je doel. 
            Kom hier regelmatig terug om gefocust te blijven op het grotere plaatje.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MissionVisionView;
